// ── Core domain types shared across the three portals ──

export type StockStatus = "in_stock" | "low_stock" | "out_of_stock";

export interface SizeStock {
  /** UK size label, e.g. "42" or "M". */
  size: string;
  qty: number;
  /**
   * Backend stock-row id for this size. Present when data comes from the live
   * API (needed to reserve a specific size); omitted in demo mode.
   */
  stockId?: string;
  /** Units currently held in carts (live API only). */
  reserved?: number;
  /** qty − reserved, as computed by the backend (live API only). */
  available?: number;
}

export type ProductCategory = "sneakers" | "official" | "casual" | "lifestyle";
export type ProductCondition = "new" | "like_new" | "good" | "fair";

export interface Product {
  id: string;
  wholesalerId: string;
  wholesalerName: string;
  wholesalerMarket?: string;
  name: string;
  brand: string;
  category: ProductCategory;
  description: string;
  condition: ProductCondition;
  imageColor: string; // seed accent for the placeholder thumbnail
  /** Real uploaded image URLs (live API); use [0] for the primary display. */
  imageUrls: string[];
  sizes: SizeStock[];
  /** Unit price in KES. */
  price: number;
  visible: boolean;
  updatedAt: string;
}

export function totalUnits(p: Product): number {
  return p.sizes.reduce((sum, s) => sum + s.qty, 0);
}

export function stockStatus(p: Product): StockStatus {
  const total = totalUnits(p);
  if (total === 0) return "out_of_stock";
  if (p.sizes.some((s) => s.qty > 0 && s.qty < 5) || total < 10) return "low_stock";
  return "in_stock";
}

export type OrderStatus = "pending" | "confirmed" | "fulfilled" | "cancelled";

export interface Order {
  id: string;
  reference: string; // e.g. "SS-1042"
  wholesalerId: string;
  wholesalerName: string;
  resellerId: string;
  resellerName: string;
  productId: string;
  productName: string;
  brand: string;
  size: string;
  qty: number;
  unitPrice: number;
  /** commissionRate as a fraction, e.g. 0.03 */
  commissionRate: number;
  status: OrderStatus;
  createdAt: string;
}

export function orderTotal(o: Order): number {
  return o.unitPrice * o.qty;
}

export function orderCommission(o: Order): number {
  return Math.round(orderTotal(o) * o.commissionRate);
}

// ── Reservations (cart hold → checkout) ──

export type ReservationStatus = "held" | "released" | "checked_out" | "expired";

export interface Reservation {
  id: string;
  productId: string;
  productName: string;
  brand: string;
  wholesalerId: string;
  wholesalerName: string;
  resellerId: string;
  resellerName: string;
  size: string;
  qty: number;
  unitPrice: number;
  commissionRate: number;
  status: ReservationStatus;
  createdAt: string;
  /** When the hold auto-releases if not checked out. */
  expiresAt: string;
  /** Backend stock-row id this hold is against (live API only). */
  stockId?: string;
}

export function reservationTotal(r: Reservation): number {
  return r.unitPrice * r.qty;
}

export function reservationCommission(r: Reservation): number {
  return Math.round(reservationTotal(r) * r.commissionRate);
}

export interface Wholesaler {
  id: string;
  name: string;
  market: string; // Kamukunji, Eastleigh, CBD…
  plan: "Free" | "Pro" | "Business";
  productCount: number;
  connected: boolean;
  /** ISO date of last stock update — drives freshness metrics. */
  lastActiveAt: string;
  rating: number;
}

export interface Reseller {
  id: string;
  name: string;
  plan: "Free" | "Starter" | "Growth";
  connections: number;
  gmvMonth: number; // KES this month
  commissionRate: number;
  joinedAt: string;
}

export interface DailyPoint {
  date: string; // ISO
  revenue: number;
  units: number;
}

export interface BestSeller {
  productId: string;
  name: string;
  brand: string;
  units: number;
  revenue: number;
}

export interface ReorderAlert {
  productId: string;
  name: string;
  size: string;
  daysLeft: number; // based on sales velocity
  urgency: "now" | "soon" | "healthy";
}

// ── Admin / market intelligence ──

export interface AuditEntry {
  id: string;
  actor: string;
  actorRole: "wholesaler" | "reseller" | "admin" | "system";
  action: string;
  target: string;
  recordId: string | null;
  oldData: Record<string, unknown> | null;
  newData: Record<string, unknown> | null;
  at: string;
}

export interface MarketTrend {
  model: string;
  brand: string;
  unitsSold: number;
  weekChange: number; // percent
}

export interface SizeRunout {
  size: string;
  demandIndex: number; // 0..100
}
